// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for updating existing assets in the system.
 * Provides functionality to modify asset properties including serial numbers,
 * product associations, ownership, and metadata.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { detailedAssetSchema } from '@/asset/api/types';
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating an asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const updateAssetResponseSchema = z
  .object({
    data: detailedAssetSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Structure for updating asset metadata fields.
 * Each update specifies a field identifier and its new value.
 *
 * @property {string} fieldId - Identifier of the metadata field to update
 * @property {string | number | null} value - New value for the field, null to clear
 */
export interface AssetMetadataUpdate {
  fieldId: string;
  value: string | number | boolean | null;
}

/**
 * Parameters for updating an asset's properties.
 * All fields are optional, allowing partial updates of asset information.
 *
 * @property {string} [serial] - New serial number for the asset
 * @property {string | null} [ownerId] - New owner identifier, null to clear
 * @property {string | null} [description] - New asset description, null to clear
 * @property {AssetFunctionalStatus} [functionalStatus] - New functional status
 * @property {DateTime} [inventoryDate] - New inventory date
 * @property {AssetMetadataUpdate[]} [metadata] - Array of metadata field updates
 */
export interface UpdateAssetParameters {
  serial?: string;
  ownerId?: string | null;
  description?: string | null;
  functionalStatus?: AssetFunctionalStatus;
  inventoryDate?: DateTime;
  metadata?: AssetMetadataUpdate[];
}

/**
 * Updates an existing asset's properties in the system.
 * This function handles the API call to modify asset properties and validates
 * the response data using Zod schemas. Only provided parameters will be updated.
 *
 * @param key - Unique identifier of the asset to update
 * @param parameters - Object containing the properties to update
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateAsset(
  key: string,
  parameters: UpdateAssetParameters
) {
  const url = `/v1/asset/${key}`;
  const response = await api.patch(url, {
    serial: parameters.serial,
    ownerId: parameters.ownerId,
    description: parameters.description,
    functionalStatus: parameters.functionalStatus,
    inInventoryDate: parameters.inventoryDate
      ? serializeDateForAPI(parameters.inventoryDate)
      : undefined,
    metadata: parameters.metadata,
  });

  return parseResponse(updateAssetResponseSchema, response.data, {
    schemaName: 'UpdateAsset',
    endpoint: url,
  });
}
